import React, { useState, useEffect, useCallback } from 'react';
import monthlyReportService from '../service/monthlyReportService.mjs';
import { REGIONS, regionLabel } from '../page/PTAERegions.mjs';
import { formatInstant, localDayEndISO } from '../../utils/dateOnly.mjs';

/**
 * Delegate monthly-report review while the auditor is away.
 *
 * Two routes to a delegate: the finance director (one click - resolved to a
 * concrete person server-side) or any other reviewing staff member.
 *
 * The delegation is REGION-SCOPED: cover for one region must not hand over every
 * region's reports, so at least one region is required. The return date is when
 * the auditor expects to be back, after which the delegation stops granting
 * anything - and it is shown only to the auditor who set it, because it
 * describes their absence rather than the delegate's mandate.
 */

/**
 * The end of access, in the reader's own timezone and naming it.
 *
 * Deliberately a full timestamp rather than a date: the cut-off is a moment,
 * and the auditor and their delegate are often in different zones - a bare
 * date would show them different days for the same delegation.
 */
export const fmtDate = (v) => formatInstant(v) || null;

/**
 * What state a delegation is in, in one badge.
 *
 * Four outcomes have to be distinguishable because they call for different
 * action: waiting on the delegate (chase them), declined (ask someone else),
 * revoked/expired (nothing), active (nothing). Returns null when it is simply
 * in force, so the common case is unadorned.
 */
// Shared with DirectorDelegationModal, whose delegations have the same states.
export const statusBadge = (d) => {
  if (d.revokedAt) return { label: 'withdrawn', tone: 'bg-gray-100 text-gray-600' };
  if (d.status === 'declined') return { label: 'declined', tone: 'bg-red-100 text-red-700' };
  if (d.status === 'pending') {
    return d.awaitingResponse
      ? { label: 'awaiting acceptance', tone: 'bg-amber-100 text-amber-800' }
      : { label: 'lapsed unanswered', tone: 'bg-gray-100 text-gray-600' };
  }
  if (!d.active) return { label: 'expired', tone: 'bg-gray-100 text-gray-600' };
  return null;
};

const DelegationModal = ({ onClose }) => {
  const [candidates, setCandidates] = useState(null);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [kind, setKind] = useState('finance_director');
  const [userId, setUserId] = useState('');
  const [regions, setRegions] = useState([]);
  const [returnDate, setReturnDate] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, d] = await Promise.all([
        monthlyReportService.getDelegationCandidates(),
        monthlyReportService.getDelegations(),
      ]);
      setCandidates(c);
      setExisting(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRegion = (r) =>
    setRegions(prev => (prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]));

  const financeDirectors = candidates?.financeDirectors || [];
  const users = candidates?.users || [];

  const canSubmit =
    regions.length > 0 &&
    // Required. The server treats a blank return date as no expiry at all
    // (AuditorDelegation.isActive only tests it when present), so an omitted
    // date grants standing access that lapses on nothing.
    !!returnDate &&
    (kind === 'finance_director' ? financeDirectors.length > 0 : !!userId) &&
    !saving;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    setError(null);
    try {
      const result = await monthlyReportService.createDelegation({
        delegateKind: kind,
        delegateUserId: kind === 'user' ? userId : undefined,
        regions,
        // Resolved to an INSTANT in this auditor's own timezone: the END of
        // their chosen day, so the named day is itself covered. Sending the bare
        // "YYYY-MM-DD" let the server parse it as midnight UTC - a different
        // moment for every picker, and the start of the day rather than its end.
        returnDate: localDayEndISO(returnDate),
        note: note.trim() || undefined,
      });
      setNotice(result.message || 'Delegation created.');
      setRegions([]);
      setReturnDate('');
      setNote('');
      setUserId('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    setError(null);
    setNotice(null);
    try {
      const result = await monthlyReportService.revokeDelegation(id);
      setNotice(result.message || 'Delegation revoked.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * A delegation that produced no cover and is finished with: the delegate let
   * it lapse without answering, or declined it. Both are dead ends the auditor
   * needs a way out of.
   */
  const canReinvite = (d) =>
    !d.revokedAt
    && (d.status === 'declined' || (d.status === 'pending' && !d.awaitingResponse));

  /** Refill the form from a dead invitation so it can be sent again. */
  const reinvite = (d) => {
    setKind(d.delegateKind === 'finance_director' ? 'finance_director' : 'user');
    setUserId(d.delegateUserId || '');
    setRegions(Array.isArray(d.regions) ? [...d.regions] : []);
    // Deliberately NOT the old return date: it is why this lapsed.
    setReturnDate('');
    setNote(d.note || '');
    setNotice(`Re-inviting ${d.delegateName || 'this person'} - set a new return date and send.`);
    setError(null);
  };

  const mine = existing.filter(d => d.isMine);
  const toMe = existing.filter(d => !d.isMine);

  return (
    <div className="fixed inset-0 z-[9200] bg-black/50 flex items-start justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-0 sm:my-8 flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-none">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Delegate report review</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Hand monthly report review to someone else while you are away
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mr-2 sm:mr-0 w-11 h-11 sm:w-auto sm:h-auto flex-shrink-0 flex items-center justify-center sm:block text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5 overflow-y-auto sm:overflow-visible">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-lg text-sm">
              {notice}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Delegate to
                  </label>
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setKind('finance_director')}
                      className={`flex-1 min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        kind === 'finance_director'
                          ? 'bg-sky-100 border-sky-400 text-sky-900'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-sky-300'
                      }`}
                    >
                      Finance Director
                      {financeDirectors.length > 0 && (
                        <span className="block text-xs font-normal opacity-80">
                          {financeDirectors[0].name}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setKind('user')}
                      className={`flex-1 min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        kind === 'user'
                          ? 'bg-sky-100 border-sky-400 text-sky-900'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-sky-300'
                      }`}
                    >
                      Another user
                    </button>
                  </div>

                  {kind === 'finance_director' && financeDirectors.length === 0 && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      No finance director is set up, so there is nobody to delegate to. Pick
                      another user instead.
                    </p>
                  )}

                  {kind === 'user' && (
                    <select
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    >
                      <option value="">Select a user…</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} - {String(u.role).replace(/_/g, ' ')}
                          {u.region ? ` (${regionLabel(u.region)})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Regions they may review <span className="text-stanford-red">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(candidates?.regions || REGIONS.map(r => r.value)).map(r => (
                      <label
                        key={r}
                        className={`flex items-center gap-2 min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          regions.includes(r)
                            ? 'bg-sky-50 border-sky-400 text-sky-900'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-sky-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={regions.includes(r)}
                          onChange={() => toggleRegion(r)}
                          className="w-5 h-5 sm:w-auto sm:h-auto flex-shrink-0 accent-sky-600"
                        />
                        {regionLabel(r)}
                      </label>
                    ))}
                  </div>
                  {regions.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Pick at least one - a delegation with no region grants nothing.
                    </p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Your return date
                    </label>
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cover runs to the end of this day in your timezone, then
                      lapses. Your delegate is shown the same moment in theirs.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      Note (optional)
                    </label>
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Included in the email"
                      className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-5 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {saving ? 'Delegating…' : 'Delegate'}
                  </button>
                </div>
              </form>

              {mine.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Delegations you have given
                  </h4>
                  <ul className="space-y-2">
                    {mine.map(d => (
                      <li
                        key={d.id}
                        className="flex flex-wrap items-start justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <div className="min-w-0 text-sm">
                          <p className="font-medium text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1 sm:block">
                            {d.delegateName}
                            {statusBadge(d) && (
                              <span className={`sm:ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${statusBadge(d).tone}`}>
                                {statusBadge(d).label}
                              </span>
                            )}
                          </p>
                          <p className="text-gray-600">
                            {(d.regions || []).map(regionLabel).join(', ')}
                          </p>
                          {/* A decline leaves you without cover, so their reason
                              is shown rather than just the outcome. */}
                          {d.status === 'declined' && d.responseReason && (
                            <p className="text-xs text-red-700 mt-0.5 break-words">
                              "{d.responseReason}"
                            </p>
                          )}
                          {d.awaitingResponse && (
                            <p className="text-xs text-amber-700 mt-0.5">
                              Review stays with you until they accept.
                            </p>
                          )}
                          {/* Shown here and nowhere else - the delegate's own
                              view of this delegation omits it. */}
                          {d.returnDate && (
                            <p className="text-xs text-gray-500">
                              You return {fmtDate(d.returnDate)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-stretch sm:items-end gap-1 flex-shrink-0 w-full sm:w-auto max-sm:empty:hidden">
                          {(d.active || d.awaitingResponse) && (
                            <button
                              onClick={() => revoke(d.id)}
                              className="min-h-[44px] sm:min-h-0 px-3 py-1 text-sm sm:text-xs border border-red-300 text-red-700 rounded-lg sm:rounded hover:bg-red-50"
                            >
                              {d.awaitingResponse ? 'Withdraw' : 'Revoke'}
                            </button>
                          )}

                          {/* An invitation that lapsed unanswered, or was
                              declined, previously offered NOTHING: it sat on the
                              list for good with no way to clear it or try again,
                              which read as "this person can no longer be chosen".
                              Re-invite refills the form with the same person and
                              regions so only a new return date is needed. */}
                          {canReinvite(d) && (
                            <button
                              onClick={() => reinvite(d)}
                              className="min-h-[44px] sm:min-h-0 px-3 py-1 text-sm sm:text-xs border border-sky-300 text-sky-700 rounded-lg sm:rounded hover:bg-sky-50"
                            >
                              Re-invite
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {toMe.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Delegated to you
                  </h4>
                  <ul className="space-y-2 text-sm">
                    {toMe.map(d => (
                      <li key={d.id} className="border border-gray-200 rounded-lg px-3 py-2">
                        <p className="font-medium text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1 sm:block">
                          <span>From {d.delegatedByName}</span>
                          {statusBadge(d) && (
                            <span className={`sm:ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${statusBadge(d).tone}`}>
                              {d.awaitingResponse ? 'awaiting your response' : statusBadge(d).label}
                            </span>
                          )}
                        </p>
                        <p className="text-gray-600">
                          {(d.regions || []).map(regionLabel).join(', ')}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DelegationModal;
